<template>
  <div class="attribute-option-picker">
    <div v-if="type === 'select'" class="select is-small is-fullwidth" :class="{ 'is-danger': hasError }">
      <select :id="inputId" :name="inputId" v-model="localSelectValue" @change="emitSelect">
        <option value="">Zgjidhni...</option>
        <option v-for="opt in localOptions" :key="opt.guid" :value="opt.guid">
          {{ opt.name }}
        </option>
      </select>
    </div>

    <VueMultiselect
      v-else
      v-model="localMultiselectValue"
      :options="localOptions"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="false"
      :preserve-search="true"
      placeholder="Zgjidhni..."
      label="name"
      track-by="guid"
      :class="{ 'is-danger-multiselect': hasError }"
    />

    <div class="option-picker-actions">
      <button
        v-if="!showAdd"
        type="button"
        class="button is-small is-light option-action-btn"
        @click="showAdd = true"
      >
        <i class="fa fa-plus mr-1"></i> Shto alternativë
      </button>
      <div v-else class="add-option-row">
        <input
          v-model="newOptionName"
          class="input is-small"
          type="text"
          placeholder="Emri i alternativës"
          @keyup.enter="createOption"
        />
        <button type="button" class="button is-small is-link" :disabled="saving" @click="createOption">
          Ruaj
        </button>
        <button type="button" class="button is-small" @click="cancelAdd">Anulo</button>
      </div>
      <button
        v-if="localOptions.length"
        type="button"
        class="button is-small is-light option-action-btn"
        @click="showManage = !showManage"
      >
        <i class="fa fa-pencil mr-1"></i> Riemëro
      </button>
    </div>

    <p class="help is-size-7 option-scope-hint">Alternativat vlejnë vetëm për këtë kategori</p>

    <div v-if="showManage" class="manage-options-panel">
      <div v-for="opt in localOptions" :key="opt.guid" class="manage-option-row">
        <template v-if="editingGuid === opt.guid">
          <input
            v-model="editName"
            class="input is-small"
            type="text"
            @keyup.enter="saveRename(opt)"
          />
          <button type="button" class="button is-small is-link" :disabled="saving" @click="saveRename(opt)">
            Ruaj
          </button>
          <button type="button" class="button is-small" @click="cancelRename">Anulo</button>
        </template>
        <template v-else>
          <span class="manage-option-name">{{ opt.name }}</span>
          <button type="button" class="button is-small is-light" @click="startRename(opt)">
            <i class="fa fa-pencil"></i>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from "vue-multiselect";
import AttributeServices from "@/services/Attribute";

export default {
  components: { VueMultiselect },
  props: {
    modelValue: {
      type: [String, Array],
      default: "",
    },
    options: {
      type: Array,
      default: () => [],
    },
    categoryId: {
      type: String,
      required: true,
    },
    attributeId: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: "select",
    },
    inputId: {
      type: String,
      default: "",
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "options-changed"],
  data() {
    return {
      localOptions: [],
      localSelectValue: "",
      localMultiselectValue: [],
      showAdd: false,
      showManage: false,
      newOptionName: "",
      editingGuid: null,
      editName: "",
      saving: false,
    };
  },
  watch: {
    options: {
      immediate: true,
      deep: true,
      handler(opts) {
        this.localOptions = (opts || []).map((o) => ({ ...o }));
        this.ensureUnmatchedModelInOptions();
        this.syncLocalFromModel();
      },
    },
    modelValue: {
      deep: true,
      handler() {
        this.ensureUnmatchedModelInOptions();
        this.syncLocalFromModel();
      },
    },
    localMultiselectValue: {
      deep: true,
      handler(selected) {
        if (this.type === "multiselect") {
          this.emitMultiselect(selected);
        }
      },
    },
  },
  methods: {
    ensureUnmatchedModelInOptions() {
      if (this.type === "multiselect") {
        const guids = Array.isArray(this.modelValue)
          ? this.modelValue
          : this.parseMultiselectValue(this.modelValue);
        for (const g of guids) {
          const raw = String(g ?? "").trim();
          if (!raw) continue;
          if (this.localOptions.some((o) => o.guid === raw)) continue;
          this.localOptions.push({ guid: raw, name: raw });
        }
        return;
      }
      const raw = String(this.modelValue || "").trim();
      if (!raw) return;
      if (this.localOptions.some((o) => o.guid === raw)) return;
      const byName = this.localOptions.find(
        (o) => String(o.name || "").trim().toLowerCase() === raw.toLowerCase()
      );
      if (byName) return;
      this.localOptions.push({ guid: raw, name: raw });
    },
    syncLocalFromModel() {
      if (this.type === "multiselect") {
        const guids = Array.isArray(this.modelValue)
          ? this.modelValue
          : this.parseMultiselectValue(this.modelValue);
        const resolved = guids.map((g) => this.resolveToOptionGuid(g)).filter(Boolean);
        this.localMultiselectValue = this.localOptions.filter((o) => resolved.includes(o.guid));
        // Emit remapped GUIDs when legacy values were resolved
        if (
          resolved.length === guids.length &&
          resolved.some((g, i) => g !== guids[i])
        ) {
          this.$emit("update:modelValue", resolved);
        }
      } else {
        const resolved = this.resolveToOptionGuid(this.modelValue || "");
        this.localSelectValue = resolved;
        if (resolved && resolved !== (this.modelValue || "")) {
          this.$emit("update:modelValue", resolved);
        }
      }
    },
    resolveToOptionGuid(value) {
      const raw = String(value ?? "").trim();
      if (!raw) return "";
      if (this.localOptions.some((o) => o.guid === raw)) return raw;
      const byName = this.localOptions.find(
        (o) => String(o.name || "").trim().toLowerCase() === raw.toLowerCase()
      );
      return byName?.guid || raw;
    },
    parseMultiselectValue(value) {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== "string") return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },
    emitSelect() {
      this.$emit("update:modelValue", this.localSelectValue);
    },
    emitMultiselect(selected) {
      const guids = (selected || []).map((o) => o.guid);
      // Guard against a feedback loop: syncLocalFromModel() sets localMultiselectValue
      // from the incoming modelValue, which triggers this deep watcher. Only emit when
      // the selection actually differs from modelValue, otherwise DaForm rebuilds the
      // field prop and we bounce back here forever (tab freeze).
      const current = Array.isArray(this.modelValue) ? this.modelValue : this.parseMultiselectValue(this.modelValue);
      if (guids.length === current.length && guids.every((g) => current.includes(g))) return;
      this.$emit("update:modelValue", guids);
    },
    cancelAdd() {
      this.showAdd = false;
      this.newOptionName = "";
    },
    async createOption() {
      const name = (this.newOptionName || "").trim();
      if (!name || this.saving) return;

      this.saving = true;
      try {
        const created = await AttributeServices.createCategoryOption({
          category_id: this.categoryId,
          attribute_id: this.attributeId,
          name,
        });
        if (created?.guid) {
          this.localOptions.push({ guid: created.guid, name: created.name });
          this.$emit("options-changed", this.localOptions);

          if (this.type === "multiselect") {
            this.localMultiselectValue = [...this.localMultiselectValue, { guid: created.guid, name: created.name }];
            this.emitMultiselect(this.localMultiselectValue);
          } else {
            this.localSelectValue = created.guid;
            this.emitSelect();
          }
        }
        this.cancelAdd();
      } finally {
        this.saving = false;
      }
    },
    startRename(opt) {
      this.editingGuid = opt.guid;
      this.editName = opt.name;
      this.showManage = true;
    },
    cancelRename() {
      this.editingGuid = null;
      this.editName = "";
    },
    async saveRename(opt) {
      const name = (this.editName || "").trim();
      if (!name || name === opt.name || this.saving) {
        this.cancelRename();
        return;
      }

      this.saving = true;
      try {
        const updated = await AttributeServices.renameCategoryOption({
          guid: opt.guid,
          name,
        });
        const idx = this.localOptions.findIndex((o) => o.guid === opt.guid);
        if (idx !== -1) {
          this.localOptions[idx].name = updated?.name || name;
          this.$emit("options-changed", this.localOptions);
        }
        if (this.type === "multiselect") {
          this.localMultiselectValue = this.localMultiselectValue.map((o) =>
            o.guid === opt.guid ? { ...o, name: updated?.name || name } : o
          );
        }
        this.cancelRename();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
.attribute-option-picker {
  width: 100%;
}

.is-fullwidth,
.is-fullwidth select {
  width: 100%;
}

.option-picker-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
}

.add-option-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}

.add-option-row .input {
  flex: 1;
  min-width: 120px;
}

.option-action-btn {
  border: none;
  background: transparent;
  color: #3273dc;
  padding-left: 0;
  padding-right: 0.25rem;
}

.option-scope-hint {
  margin-top: 0.2rem;
  color: #7a7a7a;
}

.manage-options-panel {
  margin-top: 0.35rem;
  padding: 0.5rem;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fafafa;
  max-height: 140px;
  overflow-y: auto;
}

.manage-option-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0;
}

.manage-option-name {
  flex: 1;
  font-size: 0.85rem;
}

:deep(.multiselect) {
  min-height: 32px;
  font-size: 0.85rem;
}

:deep(.multiselect__tags) {
  min-height: 32px;
  padding-top: 4px;
}

.is-danger-multiselect :deep(.multiselect__tags) {
  border-color: #b94a48;
}
</style>
